import { useTranslation } from 'react-i18next';
import { displayDigits } from '../utils/displayDigits';
import { trainingZoneColor, DEFAULT_TRAINING_ZONE_NUMBERS } from '../utils/trainingZones';
import './TrainingZoneLegend.css';

export interface TrainingZoneLegendItem {
  number: number;
  /** Athlete-defined name overrides the standard name for this zone. */
  name?: string;
  /** Keep athlete-specific ranges or names visible alongside the standard color key. */
  detail?: string;
}
interface Props {
  zones?: readonly TrainingZoneLegendItem[];
  compact?: boolean;
  className?: string;
}
const DEFAULT_ZONES = DEFAULT_TRAINING_ZONE_NUMBERS.map(number => ({ number }));

/** The same numbered color key can sit directly inside a chart row. */
export function TrainingZoneLabel({ number, name, standardName = true, ordinalLabel }: { number: number; name?: string; standardName?: boolean; ordinalLabel?: string }) {
  const { t, i18n } = useTranslation();
  const ordinal = ordinalLabel ?? t('trainingZones.legend.zone', { number: displayDigits(number, i18n.resolvedLanguage ?? i18n.language) });
  const label = name ?? (standardName ? t(`trainingZones.names.z${number}`, { defaultValue: t('trainingZones.legend.custom') }) : '');
  return <span className="training-zone-label">
    <span className="training-zone-legend__swatch" aria-hidden="true" style={{ backgroundColor: trainingZoneColor(number) }} />
    <span className="training-zone-legend__label"><span className="training-zone-legend__number">{ordinal}</span>{label && <><span aria-hidden="true"> · </span>{label}</>}</span>
  </span>;
}

/** Shared, non-interactive key: color, localized ordinal and name always travel together. */
export function TrainingZoneLegend({ zones = DEFAULT_ZONES, compact = false, className = '' }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const items = [...new Map(zones.filter(zone => Number.isInteger(zone.number) && zone.number > 0)
    .map(zone => [zone.number, zone])).values()].sort((a, b) => a.number - b.number);
  if (!items.length) return null;
  return <div className={`training-zone-legend${compact ? ' training-zone-legend--compact' : ''} ${className}`.trim()}
    role="group" aria-label={t('trainingZones.legend.title')} dir={i18n.dir()}>
    <ul className="training-zone-legend__list" role="list">
      {items.map(zone => {
        const detail = zone.detail ? displayDigits(zone.detail, locale).replace(/%/g, /^fa(?:-|$)/i.test(locale ?? '') ? '٪' : '%') : null;
        return <li key={zone.number} className="training-zone-legend__item" data-zone-number={zone.number}>
          <span className="training-zone-legend__text">
            <TrainingZoneLabel number={zone.number} name={zone.name} />
            {detail && <bdi className="training-zone-legend__detail">{detail}</bdi>}
          </span>
        </li>;
      })}
    </ul>
  </div>;
}
